import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import UploadGravacoes from './UploadGravacoes';

interface Audio {
  id: string;
  exercicio_id: string;
  exercicio_nome: string;
  audio_url: string;
  audio_titulo: string;
  audio_duracao: string;
  audio_tipo: string;
  ativo: boolean;
}

const CATEGORIAS: Record<string, { label: string; icon: string; cor: string; bg: string; imagem: string }> = {
  pnl: {
    label: 'PNL',
    icon: 'ri-brain-line',
    cor: 'from-violet-500 to-purple-600',
    bg: 'from-violet-500/10 to-purple-600/10',
    imagem: 'https://public.readdy.ai/ai/img_res/09a1379ca733960f5d0a30682103d391.jpg',
  },
  alta_performance: {
    label: 'Alta Performance',
    icon: 'ri-flashlight-line',
    cor: 'from-amber-500 to-orange-500',
    bg: 'from-amber-500/10 to-orange-500/10',
    imagem: 'https://readdy.ai/api/search-image?query=powerful%20athlete%20silhouette%20golden%20sunrise%20mountain%20peak%20energy%20lightning%20bolt%20amber%20orange%20warm%20tones%20dark%20dramatic%20sky%20achievement%20success%20motivation%20cinematic%20high%20contrast%20artistic&width=640&height=360&seq=alta-perf-cat-01&orientation=landscape',
  },
  visualizacao: {
    label: 'Visualizações',
    icon: 'ri-eye-line',
    cor: 'from-teal-500 to-cyan-500',
    bg: 'from-teal-500/10 to-cyan-500/10',
    imagem: 'https://readdy.ai/api/search-image?query=serene%20woman%20meditating%20floating%20above%20clouds%20teal%20cyan%20ethereal%20light%20rays%20dreamy%20atmosphere%20soft%20bokeh%20spiritual%20visualization%20peaceful%20sky%20abstract%20artistic%20cinematic%20photography&width=640&height=360&seq=visual-cat-01&orientation=landscape',
  },
  meditacao: {
    label: 'Meditação',
    icon: 'ri-mental-health-line',
    cor: 'from-rose-400 to-pink-500',
    bg: 'from-rose-400/10 to-pink-500/10',
    imagem: 'https://readdy.ai/api/search-image?query=lotus%20flower%20blooming%20soft%20pink%20rose%20petals%20gentle%20light%20meditation%20spiritual%20calm%20water%20reflection%20dark%20background%20zen%20garden%20tranquil%20atmosphere%20artistic%20photography%20cinematic%20bokeh&width=640&height=360&seq=medit-cat-01&orientation=landscape',
  },
};

const IMAGENS_EXERCICIO: Record<string, string> = {
  'proposito-vida': 'https://readdy.ai/api/search-image?query=person%20standing%20on%20mountain%20top%20sunrise%20golden%20light%20purpose%20life%20journey%20silhouette%20dramatic%20sky%20clouds%20inspiring%20cinematic%20warm%20tones%20dark%20foreground%20spiritual%20awakening&width=640&height=360&seq=prop-vida-01&orientation=landscape',
  'amor-ideal': 'https://readdy.ai/api/search-image?query=two%20hearts%20glowing%20soft%20pink%20rose%20gold%20light%20love%20romance%20bokeh%20dark%20background%20ethereal%20dreamy%20particles%20floating%20artistic%20cinematic%20warm%20intimate%20atmosphere&width=640&height=360&seq=amor-ideal-01&orientation=landscape',
  'prosperidade-carreira': 'https://readdy.ai/api/search-image?query=golden%20coins%20abundance%20prosperity%20career%20success%20warm%20amber%20light%20dark%20background%20luxury%20wealth%20manifestation%20artistic%20cinematic%20bokeh%20glowing%20particles&width=640&height=360&seq=prosp-carr-01&orientation=landscape',
  'saude-bem-estar': 'https://readdy.ai/api/search-image?query=healthy%20vibrant%20green%20nature%20wellness%20body%20energy%20vitality%20fresh%20morning%20light%20forest%20sunbeams%20peaceful%20healing%20cinematic%20artistic%20soft%20tones%20dark%20background&width=640&height=360&seq=saude-01&orientation=landscape',
  'identidade-alta-performance': 'https://readdy.ai/api/search-image?query=powerful%20confident%20person%20golden%20light%20high%20performance%20identity%20strength%20determination%20dark%20dramatic%20background%20cinematic%20amber%20glow%20achievement%20artistic&width=640&height=360&seq=ident-ap-01&orientation=landscape',
  'encantations-tony-robbins': 'https://readdy.ai/api/search-image?query=powerful%20affirmations%20energy%20explosion%20fire%20light%20motivation%20empowerment%20dark%20background%20golden%20sparks%20cinematic%20dramatic%20high%20contrast%20artistic%20photography&width=640&height=360&seq=encant-01&orientation=landscape',
  'ancoragem-emocional-pnl': 'https://readdy.ai/api/search-image?query=emotional%20anchor%20glowing%20neural%20connections%20violet%20purple%20mind%20body%20connection%20dark%20background%20abstract%20artistic%20cinematic%20particles%20energy%20flow&width=640&height=360&seq=ancor-01&orientation=landscape',
  'tecnica-swish-pnl': 'https://readdy.ai/api/search-image?query=mind%20transformation%20switch%20purple%20violet%20neural%20reprogramming%20abstract%20brain%20waves%20dark%20background%20cinematic%20artistic%20glowing%20particles%20energy&width=640&height=360&seq=swish-01&orientation=landscape',
  'ressignificacao-reframing-pnl': 'https://readdy.ai/api/search-image?query=reframing%20perspective%20change%20kaleidoscope%20violet%20purple%20transformation%20mind%20shift%20dark%20background%20abstract%20artistic%20cinematic%20glowing%20light&width=640&height=360&seq=ressig-01&orientation=landscape',
  'linha-tempo-pnl': 'https://readdy.ai/api/search-image?query=timeline%20past%20present%20future%20glowing%20path%20violet%20purple%20dark%20background%20abstract%20time%20travel%20cinematic%20artistic%20particles%20light%20trail&width=640&height=360&seq=linha-01&orientation=landscape',
  'submodalidades-pnl': 'https://readdy.ai/api/search-image?query=submodalities%20mind%20control%20abstract%20neural%20patterns%20violet%20purple%20dark%20background%20cinematic%20artistic%20glowing%20geometric%20shapes%20consciousness&width=640&height=360&seq=submod-01&orientation=landscape',
  'modelagem-excelencia-pnl': 'https://readdy.ai/api/search-image?query=excellence%20modeling%20mirror%20reflection%20violet%20purple%20dark%20background%20abstract%20artistic%20cinematic%20glowing%20particles%20achievement%20mastery&width=640&height=360&seq=model-01&orientation=landscape',
};

function detectarCategoria(exercicioId: string): string {
  if (
    exercicioId.includes('pnl') ||
    ['ancoragem', 'swish', 'ressignificacao', 'linha-tempo', 'submodalidades', 'modelagem'].some(
      (k) => exercicioId.includes(k)
    )
  )
    return 'pnl';
  if (exercicioId.includes('identidade') || exercicioId.includes('encantations'))
    return 'alta_performance';
  if (
    exercicioId.includes('meditacao') ||
    exercicioId.includes('guiada') ||
    exercicioId.includes('proposito') ||
    exercicioId.includes('amor-ideal') ||
    exercicioId.includes('saude') ||
    exercicioId.includes('bem-estar')
  )
    return 'meditacao';
  if (
    exercicioId.includes('prosperidade') ||
    exercicioId.includes('carreira') ||
    exercicioId.includes('visualizacao') ||
    exercicioId.includes('visualiz')
  )
    return 'visualizacao';
  return 'visualizacao';
}

function obterImagemCard(audio: Audio): string {
  const especifica = IMAGENS_EXERCICIO[audio.exercicio_id];
  if (especifica) return especifica;
  const categoria = detectarCategoria(audio.exercicio_id);
  return CATEGORIAS[categoria]?.imagem || CATEGORIAS.visualizacao.imagem;
}

function formatarTempo(s: number): string {
  if (!isFinite(s) || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function extrairYoutubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

function AudioPlayerInline({ audio, cor }: { audio: Audio; cor: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [tocando, setTocando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [tempoAtual, setTempoAtual] = useState('0:00');
  const [duracao, setDuracao] = useState('0:00');
  const [volume, setVolume] = useState(1);

  const youtubeId = extrairYoutubeId(audio.audio_url);

  // Se for YouTube, renderiza embed
  if (youtubeId) {
    return (
      <div className="px-4 pb-4 pt-2">
        <div className="rounded-xl overflow-hidden bg-black aspect-video w-full">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
            title={audio.audio_titulo}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>
      </div>
    );
  }

  // Se for arquivo de áudio direto
  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (tocando) {
      el.pause();
    } else {
      el.play().catch(() => {});
    }
    setTocando(!tocando);
  };

  const handleTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    setTempoAtual(formatarTempo(el.currentTime));
    setProgresso(el.duration ? (el.currentTime / el.duration) * 100 : 0);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuracao(formatarTempo(audioRef.current.duration));
  };

  const handleEnded = () => {
    setTocando(false);
    setProgresso(0);
    setTempoAtual('0:00');
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * el.duration;
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  return (
    <div className="px-4 pb-4 pt-2">
      <audio
        ref={audioRef}
        src={audio.audio_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setTocando(true)}
        onPause={() => setTocando(false)}
        preload="metadata"
      />

      <div className="bg-black/30 rounded-xl p-4 space-y-3">
        <div
          className="w-full h-2 bg-white/10 rounded-full cursor-pointer group"
          onClick={handleSeek}
        >
          <div
            className={`h-full rounded-full bg-gradient-to-r ${cor} transition-all duration-100 relative`}
            style={{ width: `${progresso}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            className={`w-10 h-10 rounded-full bg-gradient-to-br ${cor} flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer flex-shrink-0`}
          >
            <i className={`${tocando ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-xl`}></i>
          </button>

          <div className="flex items-center gap-1 text-xs font-mono text-white/50 flex-shrink-0">
            <span className="text-white/80">{tempoAtual}</span>
            <span>/</span>
            <span>{duracao}</span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <i className={`${volume === 0 ? 'ri-volume-mute-line' : volume < 0.5 ? 'ri-volume-down-line' : 'ri-volume-up-line'} text-white/30 text-sm`}></i>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={handleVolume}
              className="w-20 h-1 accent-white cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AudioCard({
  audio,
  isOpen,
  onToggle,
}: {
  audio: Audio;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const categoria = detectarCategoria(audio.exercicio_id);
  const cat = CATEGORIAS[categoria] || CATEGORIAS.visualizacao;
  const imagemCard = obterImagemCard(audio);

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
        isOpen
          ? 'border-white/30 bg-white/10 shadow-xl shadow-black/30'
          : 'border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20'
      }`}
    >
      {/* Thumbnail */}
      <button onClick={onToggle} className="w-full text-left cursor-pointer">
        <div className="relative w-full" style={{ paddingBottom: '52%' }}>
          <img
            src={imagemCard}
            alt={audio.audio_titulo}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>

          {/* Badge categoria */}
          <div className="absolute top-3 left-3">
            <span
              className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gradient-to-r ${cat.cor} text-white shadow-lg`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.label}
            </span>
          </div>

          {/* Duração */}
          <div className="absolute top-3 right-3">
            <span className="flex items-center gap-1 text-xs text-white/90 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
              <i className="ri-time-line text-xs"></i>
              {audio.audio_duracao}
            </span>
          </div>

          {/* Botão play central */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`w-14 h-14 rounded-full bg-gradient-to-br ${cat.cor} flex items-center justify-center shadow-2xl transition-all duration-200 ${
                isOpen ? 'scale-110 ring-4 ring-white/20' : 'hover:scale-110'
              }`}
            >
              <i
                className={`${isOpen ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-3xl`}
              ></i>
            </div>
          </div>

          {/* Título */}
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-6 bg-gradient-to-t from-black/90 to-transparent">
            <h4 className="text-white font-semibold text-sm leading-snug line-clamp-1">
              {audio.audio_titulo}
            </h4>
            <p className="text-white/50 text-xs mt-0.5 line-clamp-1">{audio.exercicio_nome}</p>
          </div>
        </div>
      </button>

      {/* Player de áudio nativo */}
      {isOpen && (
        <AudioPlayerInline audio={audio} cor={cat.cor} />
      )}
    </div>
  );
}

function CategoriaSection({
  categoriaKey,
  audios,
  audioAberto,
  onToggle,
}: {
  categoriaKey: string;
  audios: Audio[];
  audioAberto: string | null;
  onToggle: (id: string) => void;
}) {
  const cat = CATEGORIAS[categoriaKey] || CATEGORIAS.visualizacao;

  return (
    <div className="mb-10">
      <div className={`flex items-center gap-3 mb-5 p-4 rounded-2xl bg-gradient-to-r ${cat.bg} border border-white/10`}>
        <div
          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cat.cor} flex items-center justify-center shadow-lg flex-shrink-0`}
        >
          <i className={`${cat.icon} text-xl text-white`}></i>
        </div>
        <div>
          <h3 className="text-white text-xl font-bold">{cat.label}</h3>
          <p className="text-white/40 text-sm">
            {audios.length} áudio{audios.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {audios.map((audio) => (
          <AudioCard
            key={audio.id}
            audio={audio}
            isOpen={audioAberto === audio.id}
            onToggle={() => onToggle(audio.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function MinhasGravacoes() {
  const [audios, setAudios] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(true);
  const [audioAberto, setAudioAberto] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [mostrarUpload, setMostrarUpload] = useState(false);

  useEffect(() => {
    carregarAudios();
  }, []);

  const carregarAudios = async () => {
    try {
      const { data, error } = await supabase
        .from('audios_exercicios')
        .select('*')
        .eq('ativo', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAudios(data || []);
    } catch (err) {
      console.error('Erro ao carregar áudios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id: string) => {
    setAudioAberto((prev) => (prev === id ? null : id));
  };

  const audiosFiltrados = audios.filter(
    (a) =>
      busca === '' ||
      a.audio_titulo.toLowerCase().includes(busca.toLowerCase()) ||
      a.exercicio_nome.toLowerCase().includes(busca.toLowerCase())
  );

  const porCategoria: Record<string, Audio[]> = {};
  audiosFiltrados.forEach((a) => {
    const cat = detectarCategoria(a.exercicio_id);
    if (!porCategoria[cat]) porCategoria[cat] = [];
    porCategoria[cat].push(a);
  });

  const ordemCategorias = ['visualizacao', 'pnl', 'alta_performance', 'meditacao'];
  const categoriasPresentes = ordemCategorias.filter((c) => porCategoria[c]?.length > 0);

  if (loading) {
    return (
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse"></div>
          <div className="h-6 w-48 bg-white/10 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-52 rounded-2xl bg-white/5 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (audios.length === 0) {
    return (
      <div className="mb-10">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
              <i className="ri-headphone-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-white text-3xl font-bold font-['Playfair_Display']">Minhas Gravações</h2>
              <p className="text-white/30 text-sm mt-0.5">Nenhuma gravação disponível ainda</p>
            </div>
          </div>
          <button
            onClick={() => setMostrarUpload(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line text-base"></i>
            Enviar Gravações
          </button>
        </div>
        {mostrarUpload && <UploadGravacoes onClose={() => setMostrarUpload(false)} />}
      </div>
    );
  }

  return (
    <div className="mb-10">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
            <i className="ri-headphone-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-white text-3xl font-bold font-['Playfair_Display']">Minhas Gravações</h2>
            <p className="text-white/40 text-sm mt-0.5">
              {audios.length} áudio{audios.length !== 1 ? 's' : ''} disponíve{audios.length !== 1 ? 'is' : 'l'} •{' '}
              {categoriasPresentes.length} categoria{categoriasPresentes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-base"></i>
            <input
              type="text"
              placeholder="Buscar gravação..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 w-52"
            />
          </div>

          <button
            onClick={() => setMostrarUpload(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line text-base"></i>
            Enviar Gravações
          </button>
        </div>
      </div>

      {mostrarUpload && <UploadGravacoes onClose={() => setMostrarUpload(false)} />}

      {categoriasPresentes.length === 0 ? (
        <div className="text-center py-10 text-white/40">
          <i className="ri-search-line text-4xl mb-2 block"></i>
          <p className="text-lg">Nenhuma gravação encontrada para &quot;{busca}&quot;</p>
        </div>
      ) : (
        categoriasPresentes.map((cat) => (
          <CategoriaSection
            key={cat}
            categoriaKey={cat}
            audios={porCategoria[cat]}
            audioAberto={audioAberto}
            onToggle={handleToggle}
          />
        ))
      )}

      {audioAberto && (
        <div className="mt-2 flex items-center gap-2 text-white/30 text-xs">
          <i className="ri-information-line"></i>
          <span>Clique no card novamente para fechar o player.</span>
        </div>
      )}
    </div>
  );
}
